/**
 * FailedPayments.tsx — the failed-payment alerts area.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: renewals bill monthly and a capture can fail (card declined, hold
 * lapsed); the admin needs to see and chase these. GET
 * /v1/orders/admin/failed-payments (ADMIN-only) now serves the live feed —
 * every fetch has a retryable error state (TA-06) and a clear empty state when
 * there is genuinely nothing to chase. Rendered as a proper data table (column
 * headers, £ right-aligned in tabular figures, a reason chip, formatted date)
 * rather than a stacked list. This is the ADMIN surface, so money IS shown
 * (unlike the clinician-projected surfaces, which never render a price).
 *
 * THIS BOARD IS WHERE THE PAYMENT-LINK CONTROL LIVES (CTO 2026-09-04). It is
 * the one surface in the portal whose whole subject is "this patient's money did
 * not arrive", it already holds the OrderCase id the mint route keys on, it is
 * ADMIN-only end to end (so the money rule needs no second gate), and it is
 * where a desk lands when it goes looking for a failed payment. Putting the
 * control on the deal drawer instead would have parked a live Stripe page next
 * to the clinician's plan-link control, one role rule apart — see the warning
 * in PaymentLinkAction.tsx.
 *
 * THE AT-RISK / LAPSED SPLIT (orders #178, rendered here 2026-09-04). A hold row
 * now carries `holdLapsed`, and the two states need different behaviour from the
 * desk: at risk = the pre-auth is days from expiry and the money can STILL be
 * captured, so chase now; expired = the authorisation is gone and a fresh
 * payment is the only route (which is exactly what the payment link is for).
 * Rendering them identically as "Hold expired" told a desk money was lost while
 * it was still savable. The split is read from the server's flag and never
 * derived from `failedAt < now` — a skewed admin laptop would put rows on the
 * wrong side of the line.
 *
 * TEST RECORDS (2026-09-11). Under the Payments filter's "Show test records" the
 * feed is re-read with `includeTest=true` and a flagged row wears the muted Test
 * badge. It gets NO payment-link control: orders-service refuses to mint a link
 * for a test order (409 — a rehearsal "is never approved or charged"), so the
 * button could only ever fail there, and a failed "send payment link" on this
 * board reads as a patient we could not reach. A muted line says why instead.
 */
import { useCallback, useEffect, useState } from 'react';
import { getFailedPayments } from '../api/orders';
import type { PaymentFailureAlert } from '../api/types';
import { formatPence, formatUtcDate } from '../lib/format';
import { SectionTitle } from '../staff/ui';
import { DataTable, Td, Th } from '../ui/Table';
import { Alert, Badge, Card, ErrorState, Help, type BadgeTone } from '../ui/kit';
import { TableSkeleton } from '../ui/skeletons';
import { PaymentLinkAction } from './PaymentLinkAction';

/**
 * The (source, urgency) pair collapsed into ONE chip a desk can read at a
 * glance. Three states, not two:
 *
 *   Card declined  a real renewal-capture decline — chase it (red). Finer
 *                  Stripe codes are deliberately mapped to `card_declined`
 *                  server-side and are never widened here.
 *   Hold at risk   a month-1 pre-auth past the at-risk line but NOT yet past
 *                  the lapse line. The money is STILL capturable, so this is
 *                  the loudest row on the board — it is the only one where
 *                  acting today saves the payment.
 *   Hold expired   past the lapse estimate. The authorisation is gone; a fresh
 *                  payment (the payment-link action on the row) is the route.
 *                  Amber, not red: the money is recoverable, just not by
 *                  capturing this hold.
 *
 * The `reason` enum is contract-pinned to two values, so an at-risk hold still
 * arrives as `reason:'hold_lapsed'` — read `isHold` as the SOURCE and
 * `holdLapsed` as the FACT, which is why this switches on the flags and keeps
 * `reason` only as the fallback for an orders build that predates them.
 */
const DISPLAY = {
  declined: { label: 'Card declined', tone: 'danger' as BadgeTone },
  atRisk: { label: 'Hold at risk', tone: 'danger' as BadgeTone },
  expired: { label: 'Hold expired', tone: 'pending' as BadgeTone },
};

/**
 * `isHold` absent → fall back to the contract enum (it means the same thing).
 * `holdLapsed` absent → LAPSED, deliberately: on the orders builds that omit
 * it the query started AT the lapse line, so every hold row it returned really
 * had lapsed. Guessing "still savable" instead would send a desk chasing money
 * that is already gone.
 */
function alertDisplay(alert: PaymentFailureAlert): { label: string; tone: BadgeTone; atRisk: boolean } {
  const isHold = alert.isHold ?? alert.reason === 'hold_lapsed';
  if (!isHold) return { ...DISPLAY.declined, atRisk: false };
  return alert.holdLapsed === false ? { ...DISPLAY.atRisk, atRisk: true } : { ...DISPLAY.expired, atRisk: false };
}

export function FailedPayments({ showTest = false }: { showTest?: boolean }) {
  const [alerts, setAlerts] = useState<PaymentFailureAlert[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(() => {
    setStatus('loading');
    // `showTest` is a dependency of the READ, not a client-side predicate: the
    // flagged rows are not in the default response at all, so asking the server
    // again is the only way to show them (or to take them away).
    getFailedPayments(showTest)
      .then((data) => {
        setAlerts(data.items);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [showTest]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card>
      <SectionTitle>Failed-payment alerts</SectionTitle>
      {status === 'loading' ? (
        <TableSkeleton rows={4} columns={6} label="Loading failed payments…" />
      ) : status === 'error' || !alerts ? (
        <ErrorState message="Sorry — we could not load the failed-payment alerts." onRetry={load} />
      ) : alerts.length === 0 ? (
        <>
          <Alert tone="info" role="status">
            No failed payments to show.
          </Alert>
          <Help style={{ marginBottom: 0, marginTop: 'var(--gps-s-3)' }}>
            Failed renewal captures and lapsed holds will appear here as soon as they happen.
          </Help>
        </>
      ) : (
        <>
          <Help style={{ marginTop: 0 }}>
            &ldquo;Hold at risk&rdquo; means the pre-authorisation has not expired yet and the money can still be
            taken &mdash; those rows sort to the top. Once a hold has expired, or a card has declined, a payment
            link is the way back: it emails the patient a page to pay on or to put a new card on.
          </Help>
          <DataTable
            label="Failed-payment alerts"
            head={
              <>
                <Th>Order</Th>
                <Th>Reason</Th>
                <Th style={{ textAlign: 'right' }}>Amount</Th>
                <Th>Subscription</Th>
                <Th>Attempts</Th>
                {/* `failedAt` is the failure time on a decline or an expired
                    hold, but the capture DEADLINE on an at-risk one — a future
                    timestamp. The header says both so the column is not read as
                    "this already happened". */}
                <Th>Failed / deadline</Th>
                <Th>Action</Th>
              </>
            }
          >
            {alerts.map((alert) => {
              const display = alertDisplay(alert);
              return (
                <tr key={`${alert.orderCaseId}-${alert.failedAt}`}>
                  <Td>
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                      Order {alert.orderCaseId}
                      {/* The muted chip the board and the Patients directory
                          use, read off the ROW. `=== true`, because an orders
                          build from before the reveal sends no `isTest`, and
                          absent is "not told", not "not a test". */}
                      {alert.isTest === true ? <Badge tone="muted">Test</Badge> : null}
                    </span>
                  </Td>
                  <Td>
                    <Badge tone={display.tone}>{display.label}</Badge>
                  </Td>
                  <Td style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {formatPence(alert.amountPence)}
                  </Td>
                  <Td>{alert.subscriptionId ? `Subscription ${alert.subscriptionId}` : '—'}</Td>
                  <Td>Attempt {alert.attemptCount}</Td>
                  <Td>
                    {display.atRisk ? `Capture by ${formatUtcDate(alert.failedAt)}` : formatUtcDate(alert.failedAt)}
                  </Td>
                  <Td>
                    {/* ⚠️ NO PAYMENT LINK ON A TEST ROW. orders-service 409s the
                        mint for a test order, so the control could only refuse;
                        a control that can only refuse is worse than none. The
                        line says why, in words, so the missing button does not
                        read as a fault. Real rows keep their control. */}
                    {alert.isTest === true ? (
                      <span style={{ fontSize: '13px', color: 'var(--gps-muted)' }}>Test record — no payment link</span>
                    ) : (
                      /* ADMIN-ONLY BY LOCATION: this whole section 403s for
                         anyone else, and the route behind the control is
                         `@Roles('ADMIN')`. It is NOT the same control as the
                         CLINICIAN+ADMIN plan link on the deal drawer — see the
                         warning at the top of PaymentLinkAction.tsx before
                         making the two "consistent". */
                      <PaymentLinkAction orderCaseId={alert.orderCaseId} />
                    )}
                  </Td>
                </tr>
              );
            })}
          </DataTable>
        </>
      )}
    </Card>
  );
}
